import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '../../../hooks/useNavigation';
import { useIsMobile, useIsTablet } from '../../../hooks/useResponsive';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PRIVATE_ROUTES } from '../../../constants/routes';
import PackageInfo from '../../../components/PackageInfo';
import { translate } from '../../../locales';
import './NavigationMenu.css';

interface NavigationMenuProps {
  salonName?: string;
  logoUrl?: string;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  salonName,
  logoUrl
}) => {
  const { navigateToPrivate, isRouteActive } = useNavigation();
  const { logout, user } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Swipe handling state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Get translations
  const t = (key: any) => translate(key, language as 'en' | 'ua');
  
  // Use provided values or fall back to translations
  // Для клієнтів показуємо ім'я користувача, для бізнесу - також ім'я користувача
  const displaySalonName = user?.role === 'client'
    ? `${user.first_name} ${user.last_name}`
    : user?.role === 'master' || user?.role === 'owner' || user?.role === 'super_admin'
    ? `${user.last_name} ${user.first_name}`
    : (salonName || t('navigation.salon.defaultName'));

  // Меню для клієнтів
  const clientNavigationItems = [
    {
      id: 'home',
      labelKey: 'navigation.items.home',
      route: 'HOME' as const,
      icon: 'bi-house',
      collapsedIcon: 'bi-house'
    },
    {
      id: 'my-records',
      labelKey: 'navigation.items.myRecords',
      route: 'MY_RECORDS' as const,
      icon: 'bi-calendar-check',
      collapsedIcon: 'bi-calendar-check'
    },
    {
      id: 'favorite-masters',
      labelKey: 'navigation.items.favoriteMasters',
      route: 'FAVORITE_MASTERS' as const,
      icon: 'bi-heart',
      collapsedIcon: 'bi-heart'
    },
    {
      id: 'visit-history',
      labelKey: 'navigation.items.visitHistory',
      route: 'VISIT_HISTORY' as const,
      icon: 'bi-clock-history',
      collapsedIcon: 'bi-clock-history'
    },
    {
      id: 'reviews',
      labelKey: 'navigation.items.reviews',
      route: 'REVIEWS' as const,
      icon: 'bi-star',
      collapsedIcon: 'bi-star'
    },
    {
      id: 'subscription',
      labelKey: 'navigation.items.subscription',
      route: 'SUBSCRIPTION' as const,
      icon: 'bi-credit-card',
      collapsedIcon: 'bi-credit-card'
    },
    {
      id: 'support',
      labelKey: 'navigation.items.support',
      route: 'SUPPORT' as const,
      icon: 'bi-question-circle',
      collapsedIcon: 'bi-question-circle'
    },
  ];

  // Меню для майстрів
  const masterNavigationItems = [
    {
      id: 'home',
      labelKey: 'navigation.items.home',
      route: 'HOME' as const,
      icon: 'bi-house',
      collapsedIcon: 'bi-house'
    },
    {
      id: 'appointments',
      labelKey: 'navigation.items.appointments',
      route: 'APPOINTMENTS' as const,
      icon: 'bi-calendar-check',
      collapsedIcon: 'bi-calendar-check'
    },
    {
      id: 'my-clients',
      labelKey: 'navigation.items.myClients',
      route: 'CLIENTS' as const,
      icon: 'bi-people',
      collapsedIcon: 'bi-people'
    },
    {
      id: 'my-services',
      labelKey: 'navigation.items.myServices',
      route: 'SERVICES' as const,
      icon: 'bi-scissors',
      collapsedIcon: 'bi-scissors'
    },
    {
      id: 'reviews',
      labelKey: 'navigation.items.reviews',
      route: 'REVIEWS' as const,
      icon: 'bi-star',
      collapsedIcon: 'bi-star'
    },
    {
      id: 'subscription',
      labelKey: 'navigation.items.subscription',
      label: user?.role === 'master' ? 'Плани послуг' : undefined,
      route: 'SUBSCRIPTION' as const,
      icon: 'bi-credit-card',
      collapsedIcon: 'bi-credit-card'
    },
    {
      id: 'support',
      labelKey: 'navigation.items.support',
      route: 'SUPPORT' as const,
      icon: 'bi-question-circle',
      collapsedIcon: 'bi-question-circle'
    },
  ];



  // Меню для власників салону
  const ownerNavigationItems = [
    {
      id: 'home',
      labelKey: 'navigation.items.home',
      route: 'HOME' as const,
      icon: 'bi-house',
      collapsedIcon: 'bi-house'
    },
    {
      id: 'dashboard',
      labelKey: 'navigation.items.dashboard',
      route: 'DASHBOARD' as const,
      icon: 'bi-speedometer2',
      collapsedIcon: 'bi-speedometer2'
    },
    {
      id: 'appointments',
      labelKey: 'navigation.items.appointments',
      route: 'APPOINTMENTS' as const,
      icon: 'bi-calendar-check',
      collapsedIcon: 'bi-calendar-check'
    },
    {
      id: 'clients',
      labelKey: 'navigation.items.clients',
      route: 'CLIENTS' as const,
      icon: 'bi-people',
      collapsedIcon: 'bi-people'
    },
    {
      id: 'statistics',
      labelKey: 'navigation.items.statistics',
      route: 'STATISTICS' as const,
      icon: 'bi-bar-chart',
      collapsedIcon: 'bi-bar-chart'
    },
    {
      id: 'services',
      labelKey: 'navigation.items.services',
      route: 'SERVICES' as const,
      icon: 'bi-scissors',
      collapsedIcon: 'bi-scissors'
    },
    {
      id: 'staff',
      labelKey: 'navigation.items.employees',
      route: 'STAFF' as const,
      icon: 'bi-person-badge',
      collapsedIcon: 'bi-person-badge'
    },
    {
      id: 'schedule',
      labelKey: 'navigation.items.schedule',
      route: 'SETTINGS_SCHEDULE' as const,
      icon: 'bi-calendar-week',
      collapsedIcon: 'bi-calendar-week'
    },
    {
      id: 'site',
      labelKey: 'navigation.items.site',
      route: 'SETTINGS_SITE' as const,
      icon: 'bi-globe',
      collapsedIcon: 'bi-globe'
    },
    {
      id: 'subscription',
      labelKey: user?.role === 'owner' ? null : 'navigation.items.subscription',
      label: user?.role === 'owner' ? 'Плани послуг' : undefined,
      route: 'SUBSCRIPTION' as const,
      icon: 'bi-credit-card',
      collapsedIcon: 'bi-credit-card'
    },
    {
      id: 'support',
      labelKey: 'navigation.items.support',
      route: 'SUPPORT' as const,
      icon: 'bi-question-circle',
      collapsedIcon: 'bi-question-circle'
    },
  ];

  // Меню для супер адміністраторів (включає всі пункти власників плюс адміністративні)
  const superAdminNavigationItems = [
    {
      id: 'home',
      labelKey: 'navigation.items.home',
      route: 'HOME' as const,
      icon: 'bi-house',
      collapsedIcon: 'bi-house'
    },
    {
      id: 'admin-support',
      labelKey: 'navigation.items.adminSupport',
      route: 'ADMIN_SUPPORT' as const,
      icon: 'bi-chat-dots',
      collapsedIcon: 'bi-chat-dots'
    },
    {
      id: 'admin-businesses',
      labelKey: 'navigation.items.adminBusinesses',
      route: 'ADMIN_BUSINESSES' as const,
      icon: 'bi-building-gear',
      collapsedIcon: 'bi-building-gear'
    },
    {
      id: 'admin-clients',
      labelKey: 'navigation.items.adminClients',
      route: 'ADMIN_CLIENTS' as const,
      icon: 'bi-people-fill',
      collapsedIcon: 'bi-people-fill'
    },
    ...ownerNavigationItems.filter(item => !['support', 'dashboard', 'appointments', 'clients', 'site', 'schedule', 'services', 'home'].includes(item.id)), // Remove specified items from owner items
  ];

  // Вибираємо меню залежно від ролі користувача
  const navigationItems = user?.role === 'client'
    ? clientNavigationItems
    : user?.role === 'master'
    ? masterNavigationItems
    : user?.role === 'super_admin'
    ? superAdminNavigationItems
    : ownerNavigationItems;



  // Налаштування для майстрів
  const masterSettingsItems = [
    {
      id: 'business',
      labelKey: 'navigation.items.business',
      route: 'SETTINGS_BUSINESS' as const,
      icon: 'bi-building'
    },
    {
      id: 'schedule',
      labelKey: 'navigation.items.scheduleSettings',
      route: 'SETTINGS_SCHEDULE' as const,
      icon: 'bi-calendar-week'
    },
    {
      id: 'site',
      labelKey: 'navigation.items.site',
      route: 'SETTINGS_SITE' as const,
      icon: 'bi-globe'
    }
  ];

  // Налаштування для власників
  const ownerSettingsItems = [
    {
      id: 'business',
      labelKey: 'navigation.items.business',
      route: 'SETTINGS_BUSINESS' as const,
      icon: 'bi-building'
    },
    {
      id: 'customization',
      labelKey: 'navigation.items.customization',
      route: 'SETTINGS_CUSTOMIZATION' as const,
      icon: 'bi-palette'
    }
  ];

  // Налаштування для супер адміністраторів (включає всі налаштування власників плюс категорії, пакети та графік)
  const superAdminSettingsItems = [
    {
      id: 'business',
      labelKey: 'navigation.items.business',
      route: 'SETTINGS_BUSINESS' as const,
      icon: 'bi-building'
    },
    {
      id: 'packages',
      labelKey: 'navigation.items.packages',
      route: 'SETTINGS_PACKAGES' as const,
      icon: 'bi-box-seam'
    },
    {
      id: 'schedule',
      labelKey: 'navigation.items.scheduleSettings',
      route: 'SETTINGS_SCHEDULE' as const,
      icon: 'bi-calendar-week'
    },
    {
      id: 'admin-cities',
      labelKey: 'navigation.items.adminCities',
      route: 'ADMIN_CITIES' as const,
      icon: 'bi-geo-alt'
    },
    {
      id: 'categories',
      labelKey: 'navigation.items.categories',
      route: 'SETTINGS_CATEGORIES' as const,
      icon: 'bi-tags'
    },
    {
      id: 'currency',
      labelKey: 'navigation.items.currency',
      route: 'SETTINGS_CURRENCY' as const,
      icon: 'bi-currency-exchange'
    },
    {
      id: 'customization',
      labelKey: 'navigation.items.customization',
      route: 'SETTINGS_CUSTOMIZATION' as const,
      icon: 'bi-palette'
    },
    {
      id: 'admin-clear',
      labelKey: 'navigation.items.adminClear',
      route: 'ADMIN_CLEAR' as const,
      icon: 'bi-trash3'
    }
  ];

  // Вибираємо налаштування залежно від ролі
  const settingsItems = user?.role === 'master'
    ? masterSettingsItems
    : user?.role === 'owner'
    ? ownerSettingsItems
    : user?.role === 'super_admin'
    ? superAdminSettingsItems
    : [];

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      console.log('🚪 Ініціалізація logout...');
      await logout();
    } catch (error) {
      console.error('❌ Помилка при logout:', error);
      // logout() вже обробляє помилку та перенаправлення
    }
  };

  const isActiveRoute = (route: string) => {
    return location.pathname === route || isRouteActive(route);
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/settings');
  };

  const handleNavItemClick = (route: keyof typeof PRIVATE_ROUTES) => {
    navigateToPrivate(route);
    // Закриваємо мобільне меню тільки на мобільних пристроях
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Swipe gesture handlers for mobile menu
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || !isMobileOpen) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isMobileOpen) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isMobileOpen || !touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // Minimum swipe distance

    if (isLeftSwipe) {
      setIsMobileOpen(false);
    }
  };

  // При переході між мобільним та десктопом скидаємо стан mobile menu
  React.useEffect(() => {
    if (!isMobile) {
      // Якщо перейшли на десктоп/планшет - закриваємо мобільне меню
      setIsMobileOpen(false);
    }
    // Якщо перейшли на планшет - скидаємо колапс, щоб меню було розгорнуте
    if (isTablet) {
      setIsCollapsed(false);
    }
  }, [isMobile, isTablet]);

  // Sync collapsed state to body class to allow layout CSS to react without relying on :has
  React.useEffect(() => {
    const body = document.body;
    
    if (isCollapsed && !isMobile) {
      body.classList.add('sidebar-collapsed');
    } else {
      body.classList.remove('sidebar-collapsed');
    }
    return () => {
      body.classList.remove('sidebar-collapsed');
    };
  }, [isCollapsed, isMobile]);

  // Sync mobile open state to body class for mobile overlay/layout
  React.useEffect(() => {
    const body = document.body;
    
    if (isMobileOpen && isMobile) {
      body.classList.add('mobile-menu-open');
    } else {
      body.classList.remove('mobile-menu-open');
    }
    return () => {
      body.classList.remove('mobile-menu-open');
    };
  }, [isMobileOpen, isMobile]);

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button 
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Відкрити меню"
        >
          <i className="bi bi-list"></i>
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && isMobileOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Navigation Sidebar */}
      <nav
        className={`
          navigation-sidebar
          ${isCollapsed ? 'collapsed' : ''}
          ${isMobileOpen ? 'mobile-open' : ''}
          ${isMobile ? 'mobile' : ''}
          ${isTablet ? 'tablet' : ''}
        `}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="sidebar-header">
          {!isCollapsed ? (
            <div className="sidebar-header-content">
              <h2>{displaySalonName}</h2>
              <PackageInfo compact={isCollapsed} />
              {/* <p>{displaySalonType}</p> */}
            </div>
          ) : (
            <button 
              className="collapsed-logo-button"
              onClick={toggleCollapse}
              aria-label="Розгорнути меню"
            >
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="collapsed-logo-img" />
              ) : (
                <i className="bi bi-list"></i>
              )}
            </button>
          )}
          
          {/* Toggle Button - показуємо на мобільних кнопку закриття, на десктопі - згортання */}
          {isMobile ? (
            <button 
              className="mobile-close-button"
              onClick={() => setIsMobileOpen(false)}
              aria-label="Закрити меню"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          ) : !isCollapsed && (
            <button 
              className="collapse-toggle"
              onClick={toggleCollapse}
              aria-label="Згорнути меню"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <ul className="navigation-list">

          {navigationItems.map((item) => (
            <li key={item.id}>
              <button
                className={`nav-button ${isActiveRoute(PRIVATE_ROUTES[item.route as keyof typeof PRIVATE_ROUTES]) ? 'active' : ''}`}
                onClick={() => handleNavItemClick(item.route as keyof typeof PRIVATE_ROUTES)}
                title={isCollapsed ? (item.labelKey ? t(item.labelKey) : (item as any).label || '') : ''}
              >
                <span className="nav-icon">
                  <i className={isCollapsed ? item.collapsedIcon : item.icon}></i>
                </span>
                {!isCollapsed && (
                  <span className="nav-label">{item.labelKey ? t(item.labelKey) : (item as any).label}</span>
                )}
              </button>
            </li>
          ))}
          
          {/* Settings with Submenu (для майстрів, власників та супер адміністраторів) */}
          {(user?.role === 'master' || user?.role === 'owner' || user?.role === 'super_admin') && settingsItems.length > 0 && (
            <li className="nav-item-with-submenu">
              <button
                className={`nav-button ${isSettingsActive() ? 'active' : ''}`}
                onClick={toggleSettings}
                title={isCollapsed ? (user?.role === 'master' ? 'Мої налаштування' : t('navigation.items.settings')) : ''}
              >
                <span className="nav-icon">
                  <i className="bi-gear"></i>
                </span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">
                      {user?.role === 'master' ? 'Мої налаштування' : t('navigation.items.settings')}
                    </span>
                    <i className={`submenu-arrow bi ${isSettingsOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </>
                )}
              </button>

              {/* Submenu */}
              {!isCollapsed && isSettingsOpen && (
                <ul className="submenu">
                  {settingsItems.map((subItem) => (
                    <li key={subItem.id}>
                      <button
                        className={`submenu-button ${isActiveRoute(PRIVATE_ROUTES[subItem.route as keyof typeof PRIVATE_ROUTES]) ? 'active' : ''}`}
                        onClick={() => handleNavItemClick(subItem.route as keyof typeof PRIVATE_ROUTES)}
                      >
                        <span className="nav-icon">
                          <i className={subItem.icon}></i>
                        </span>
                        <span className="nav-label">{t(subItem.labelKey)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}


        </ul>

        {/* Footer with Logout */}
        <div className="sidebar-footer">
          <button 
            id="logoutBtn"
            className='logout-button'
            onClick={handleLogout}
            disabled={isLoggingOut}
            title={isCollapsed ? t('navigation.actions.logout') : ''}
          >
            <span className="nav-icon">
              <i className={isLoggingOut ? 'bi bi-hourglass-split' : 'bi bi-box-arrow-right'}></i>
            </span>
            {!isCollapsed && (
              <span className="nav-label">
                {isLoggingOut ? t('navigation.actions.loggingOut') : t('navigation.actions.logout')}
              </span>
            )}
          </button>
        </div>
      </nav>
    </>
  );
};

export default NavigationMenu;
